import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Download, Eye, Image as ImageIcon, Film, FileSpreadsheet, Folder } from 'lucide-react';
import { BASE_URL } from '@/lib/API';

export const StageUploadsAccordion = ({ 
  title, 
  files = [], 
  colorTheme = 'slate',
  onPreview,
  downloadPrefix = '/api/lead-uploads/stream'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasData = files.length > 0;

  // Theme mapping
  const themes = {
    yellow: { bg: 'bg-yellow-50/40', border: 'border-yellow-100/60', text: 'text-yellow-600', dot: 'bg-yellow-500', hover: 'hover:bg-yellow-50/60', iconText: 'text-yellow-400' },
    cyan: { bg: 'bg-cyan-50/40', border: 'border-cyan-100/60', text: 'text-cyan-600', dot: 'bg-cyan-500', hover: 'hover:bg-cyan-50/60', iconText: 'text-cyan-400' },
    purple: { bg: 'bg-purple-50/40', border: 'border-purple-100/60', text: 'text-purple-600', dot: 'bg-purple-500', hover: 'hover:bg-purple-50/60', iconText: 'text-purple-400' },
    slate: { bg: 'bg-slate-50/30', border: 'border-slate-100/50', text: 'text-slate-500', dot: 'bg-emerald-500', hover: 'hover:bg-slate-50/60', iconText: 'text-slate-400' }
  };

  const theme = themes[colorTheme] || themes.slate;

  return (
    <div className={`${theme.bg} rounded-xl border ${theme.border} overflow-hidden mt-6`}>
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer ${theme.hover} transition-colors`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={`text-[10px] ${theme.text} font-bold uppercase tracking-wider flex items-center gap-2`}>
          <span className={`w-1 h-3 ${theme.dot} rounded-full`} />
          {title} {hasData ? `Available (${files.length} files)` : 'Not Available'}
        </div>
        <div className="flex items-center gap-3">
          {isOpen ? <ChevronUp size={16} className={theme.iconText} /> : <ChevronDown size={16} className={theme.iconText} />}
        </div>
      </div>
      
      {isOpen && (
        <div className={`px-4 pb-4 border-t ${theme.border.replace('border-', 'border-t-').replace('/60', '/50')} pt-3`}>
          {hasData ? (
            <div className="flex flex-col gap-3">
              <div className="border border-slate-200 bg-white rounded p-3">
                <h5 className="text-[11px] font-bold text-slate-600 mb-2 border-b pb-1">Uploaded Files</h5>
                <div className="flex flex-col gap-1.5">
                  {files.map((file, idx) => {
                    // Mimetype handling can vary depending on whether it's from VerveContact.files (type) or LeadUpload (mimetype)
                    const mimetype = file.mimetype || file.type || '';
                    const filename = file.filename || file.name || 'Unknown File';
                    
                    const isImage = mimetype.startsWith('image/');
                    const isVideo = mimetype.startsWith('video/');
                    const isPdf = mimetype === 'application/pdf';
                    const isExcel = mimetype.includes('spreadsheet') || mimetype.includes('excel') || filename.endsWith('.xls') || filename.endsWith('.xlsx');
                    
                    return (
                      <div 
                        key={file._id || idx}
                        className={`flex items-center gap-2 text-[11px] ${theme.text} bg-slate-50 p-2 rounded border border-slate-100 transition-colors w-full text-left group hover:bg-slate-100`}
                      >
                        {isImage ? <ImageIcon size={14} className="text-blue-500 shrink-0" /> : 
                         isVideo ? <Film size={14} className="text-purple-500 shrink-0" /> : 
                         isPdf ? <FileText size={14} className="text-red-500 shrink-0" /> : 
                         isExcel ? <FileSpreadsheet size={14} className="text-emerald-500 shrink-0" /> : 
                         <Folder size={14} className="text-amber-500 shrink-0" />}
                         
                        <span className="truncate flex-1" title={filename}>{filename}</span>
                        
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onPreview) {
                                onPreview({ ...file, downloadPrefix });
                              } else {
                                const rawUrl = file.url || file.fileUrl;
                                const url = rawUrl ? (rawUrl.startsWith('http') ? rawUrl : `${BASE_URL}${rawUrl}`) : `${BASE_URL}${downloadPrefix}/${file.fileId}`;
                                window.open(url, '_blank');
                              }
                            }}
                            className={`w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-400 hover:text-brand-blue transition-all border border-slate-100 shadow-sm`}
                            title="Preview"
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              import('@/lib/API').then(({ downloadFile }) => {
                                const rawUrl = file.url || file.fileUrl;
                                const url = rawUrl ? (rawUrl.startsWith('http') ? rawUrl : `${BASE_URL}${rawUrl}`) : `${BASE_URL}${downloadPrefix}/${file.fileId}?download=1`;
                                downloadFile(url, filename);
                              });
                            }}
                            className={`w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-400 hover:text-emerald-600 transition-all border border-slate-100 shadow-sm`}
                            title="Download"
                          >
                            <Download size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 italic text-center py-2">
              No files have been uploaded for this stage yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
